import { eachDayOfInterval } from "date-fns";
import { REVIEWS_COLORS } from "./constants";
import {
  formatLocalDateParam,
  parseLocalDateParam,
} from "../../utils/analyticsDateUtils";
import type {
  CategoryKey,
  CategoryRatingRow,
  PreCheckoutTrendPoint,
  RatingTrendPoint,
  ReviewsSourceSplitView,
  StarDistributionSegment,
} from "./types";

export const DEFAULT_CATEGORY_KEYS: CategoryKey[] = [
  "purity",
  "comfort",
  "service",
];

const STAR_LABELS: Record<number, string> = {
  5: "5 stars",
  4: "4 stars",
  3: "3 stars",
  2: "2 stars",
  1: "1 star",
};

const STAR_COLOR_BY_VALUE: Record<number, string> = {
  5: REVIEWS_COLORS.star5,
  4: REVIEWS_COLORS.star4,
  3: REVIEWS_COLORS.star3,
  2: REVIEWS_COLORS.star2,
  1: REVIEWS_COLORS.star1,
};

export function createEmptyStarDistribution(): StarDistributionSegment[] {
  return [5, 4, 3, 2, 1].map((star) => ({
    key: `star${star}`,
    name: STAR_LABELS[star],
    value: 0,
    fill: STAR_COLOR_BY_VALUE[star],
  }));
}

export function createEmptyCategoryBreakdowns(): Record<
  CategoryKey,
  CategoryRatingRow[]
> {
  const rows: CategoryRatingRow[] = [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    percent: 0,
  }));

  return {
    purity: rows,
    comfort: rows,
    service: rows,
  };
}

export function createEmptyCategoryAverages(): Record<CategoryKey, number> {
  return {
    purity: 0,
    comfort: 0,
    service: 0,
  };
}

export function createEmptySourceSplit(): ReviewsSourceSplitView {
  return {
    preCheckoutCount: 0,
    postStayCount: 0,
    total: 0,
  };
}

function listDaysInRange(from: string, to: string): string[] {
  const start = parseLocalDateParam(from);
  const end = parseLocalDateParam(to);

  if (!start || !end || start > end) {
    return [];
  }

  return eachDayOfInterval({ start, end }).map((day) =>
    formatLocalDateParam(day),
  );
}

export function buildEmptyPreCheckoutTrend(
  from: string,
  to: string,
): PreCheckoutTrendPoint[] {
  const days = listDaysInRange(from, to);

  return days.map((day) => ({
    day,
    postStayReviews: 0,
    preCheckoutReviews: 0,
  }));
}

export function buildEmptyRatingTrend(
  from: string,
  to: string,
  options: {
    categoriesSupported: boolean;
    categoryKeys: CategoryKey[];
  },
): RatingTrendPoint[] {
  const days = listDaysInRange(from, to);

  return days.map((day) => {
    const point: RatingTrendPoint = {
      day,
      overallRating: 0,
    };

    if (options.categoriesSupported) {
      for (const key of options.categoryKeys) {
        point[key] = 0;
      }
    }

    return point;
  });
}
